import {
  isResidenceType,
  residenceTypeFromReferenceComplement,
  type ResidenceType,
  type SurveyV2,
} from '@ghp/domain'
import { Card, CardBody, CardHeader, CountInput, Field, Select } from '@ghp/ui'
import { Home } from 'lucide-react'

import { NumberField, TextField } from './SurveyForm'
import s from './SurveyForm.module.css'

/**
 * #118 — les types de résidence, dans les mots de la fiche. Les trois premiers sont ceux que le
 * complément de référence propose (E / M / C) ; les trois suivants viennent de la même note de
 * terrain (« Industriel = Maison / Professionnel / Immeuble »).
 */
const RESIDENCE_TYPE_OPTIONS: readonly { value: ResidenceType; label: string }[] = [
  { value: 'floor', label: 'Étage' },
  { value: 'house', label: 'Maison' },
  { value: 'business', label: 'Commerce' },
  { value: 'apartment_building', label: 'Immeuble' },
  { value: 'professional', label: 'Professionnel' },
  { value: 'industrial', label: 'Industriel' },
]

/**
 * Le retour en arrière, que tous les autres contrôles de la fiche portent déjà
 * (`PRESENCE_OPTIONS`, `PRESENT_OPTIONS`, `KIND_OPTIONS`) et que celui-ci n'avait pas : un agent
 * qui tape « Commerce » par erreur sur un appartement doit pouvoir revenir à « rien » (M21).
 *
 * ⚠️ Une sentinelle d'écran, jamais écrite dans le relevé — la fonction de rappel la retraduit en
 * `null`. `Select` n'expose pas d'autre moyen d'effacer une sélection.
 */
const UNSET_RESIDENCE_TYPE = 'unset'

const UNSET_OPTION = { value: UNSET_RESIDENCE_TYPE, label: 'Non renseigné' }

/**
 * Les décomptes du logement. Libellés en français, clés du schéma en anglais — la frontière
 * du dépôt passe entre l'identifiant et la phrase, pas entre le technique et le métier.
 */
const DWELLING_FIELDS = [
  { key: 'constructionYear', label: 'Année de construction' },
  { key: 'connectedDwellingsCount', label: 'Logements raccordés' },
  { key: 'mainRoomsCount', label: 'Pièces principales' },
  { key: 'occupantsCount', label: 'Occupants' },
  { key: 'presenceYears', label: 'Présence — années' },
  { key: 'presenceMonths', label: 'Présence — mois' },
] as const

/**
 * Les clés que cet écran écrit, **énumérées**.
 *
 * ⚠️ `Partial<NonNullable<SurveyV2['dwelling']>>` ne referme rien : le schéma est un
 * `looseObject`, donc son type inféré porte un catchall `{ [k: string]: unknown }` et
 * `setDwelling({ residencetype: 'floor' })` — minuscule — compilait, écrivait une clé parasite
 * dans le brouillon, passait le schéma de dépôt et atterrissait dans le JSONB, le vrai champ
 * restant vide. `Pick` rétablit la fermeture que l'ancienne signature par clé donnait.
 */
type DwellingPatch = Partial<
  Pick<
    NonNullable<SurveyV2['dwelling']>,
    (typeof DWELLING_FIELDS)[number]['key'] | 'residenceType' | 'referenceComplement'
  >
>

export interface DwellingCardProps {
  survey: SurveyV2
  /** Fiche transmise : tout se lit, rien ne se saisit (D4). */
  disabled?: boolean
  onChange?: (survey: SurveyV2) => void
}

/**
 * « Caractéristiques de l'habitation contrôlée » — **dans l'onglet Infos depuis #350**, dans l'ordre
 * du rapport papier (propriétaire, occupant, habitation). Elle vivait dans le relevé : elle s'y est
 * **déplacée**, elle n'y est pas recopiée — deux saisies d'un même décompte se contrediraient.
 *
 * ⚠️ **Un bloc à part, et c'est voulu** : le réglage par client (#355) la masque pour Nantes
 * Métropole, qui ne la demande pas (fiche d'ajustement 03). Les décomptes vont toujours dans
 * `survey.dwelling` : rien ne change pour le relevé transmis.
 */
export function DwellingCard({ survey, disabled, onChange }: DwellingCardProps) {
  const dwelling = survey.dwelling ?? {}
  const residenceType = dwelling.residenceType ?? null
  const proposedResidenceType = residenceTypeFromReferenceComplement(dwelling.referenceComplement)

  function setDwelling(patch: DwellingPatch): void {
    onChange?.({ ...survey, dwelling: { ...survey.dwelling, ...patch } })
  }

  /**
   * Un décompte, écrit sous une clé venue de {@link DWELLING_FIELDS}. La clé est **déjà** contrainte
   * à l'union fermée par le type du paramètre : la clé calculée est sûre, et c'est tout ce que
   * l'assertion dit — une faute de frappe, elle, ne compile plus au point d'appel.
   */
  function setCount(key: (typeof DWELLING_FIELDS)[number]['key'], value: number | null): void {
    setDwelling({ [key]: value } as DwellingPatch)
  }

  /**
   * ⚠️ Le complément **propose** le type de résidence, il ne l'impose pas — mais il corrige ce
   * qu'il a lui-même proposé. Il ne remplit que la case vide **ou celle qui vient de lui** :
   * corriger `C2` en `E1D` met le type à jour, alors qu'un type choisi à la main est conservé.
   * Sans ce rattrapage, la fiche partait en « Commerce » pour un étage, avec une aide grise qui
   * disait le contraire — un constat de terrain qui ne se corrige pas sur la tablette est
   * exactement ce que M21 cherche à éviter.
   */
  function setReferenceComplement(referenceComplement: string | null): void {
    const previous = residenceTypeFromReferenceComplement(dwelling.referenceComplement)
    const proposed = residenceTypeFromReferenceComplement(referenceComplement)
    const cameFromComplement = residenceType === null || residenceType === previous

    setDwelling({
      referenceComplement,
      residenceType: cameFromComplement ? proposed : residenceType,
    })
  }

  return (
    <Card>
      <CardHeader icon={<Home size={19} />} title="Caractéristiques de l’habitation contrôlée" />
      <CardBody>
        <div className={s.fields}>
          {DWELLING_FIELDS.map((field) =>
            field.key === 'constructionYear' ? (
              <NumberField
                key={field.key}
                label={field.label}
                // Non contrôlé : la valeur vit dans le DOM le temps de la frappe, ce qui laisse
                // l'agent taper « 197 » sans qu'un relevé partiellement écrit lui soit réécrit
                // sous les doigts. Le brouillon, lui, est mis à jour à chaque touche.
                defaultValue={dwelling[field.key] ?? null}
                disabled={disabled}
                onValueChange={(value) => setCount(field.key, value)}
              />
            ) : (
              // #258 — un décompte, donc le compteur des matrices : un seul composant de décompte.
              <Field key={field.key} label={field.label}>
                {({ id }) => (
                  <CountInput
                    id={id}
                    label={field.label}
                    value={dwelling[field.key] ?? null}
                    disabled={disabled}
                    onValueChange={(value) => setCount(field.key, value)}
                  />
                )}
              </Field>
            ),
          )}
          <TextField
            label="Complément de référence"
            hint="E = Étage · M = Maison · C = Commerce"
            defaultValue={dwelling.referenceComplement ?? null}
            disabled={disabled}
            onValueChange={setReferenceComplement}
          />
          <ResidenceTypeField
            value={residenceType}
            proposed={proposedResidenceType}
            complement={dwelling.referenceComplement ?? null}
            disabled={disabled}
            onValueChange={(value) => setDwelling({ residenceType: value })}
          />
        </div>
      </CardBody>
    </Card>
  )
}

/**
 * #118 — le type de résidence, **proposé** par le complément de référence et choisi par l'agent.
 *
 * ⚠️ Un complément inconnu ne bloque rien et ne se tait pas non plus : la note dit pourquoi rien
 * n'a été proposé, plutôt que de laisser l'agent devant une case vide sans explication (M21).
 */
function ResidenceTypeField({
  value,
  proposed,
  complement,
  disabled,
  onValueChange,
}: {
  value: string | null
  proposed: ResidenceType | null
  complement: string | null
  disabled?: boolean
  onValueChange: (value: string | null) => void
}) {
  /**
   * ⚠️ Le cas courant — `value === null`, un relevé neuf — passe par la **même** branche que le
   * vocabulaire connu, et c'est ce qui garde l'identité du tableau constante d'un rendu à l'autre.
   * Le test précédent (`.some(…)`) était faux pour `null` : il tombait toujours dans l'autre
   * branche, qui produisait alors exactement la même liste, mais neuve à chaque rendu — passée en
   * `items` à Base UI, elle défaisait toute mémoïsation en aval.
   *
   * Une valeur héritée d'un relevé v1 (« principale », saisie libre) reste sélectionnable : la
   * retirer de la liste l'effacerait au premier changement d'un autre champ (M21).
   */
  const known = isResidenceType(value) || value === null
  const options = known
    ? [...RESIDENCE_TYPE_OPTIONS, UNSET_OPTION]
    : [...RESIDENCE_TYPE_OPTIONS, { value, label: value }, UNSET_OPTION]

  const proposedLabel = RESIDENCE_TYPE_OPTIONS.find((option) => option.value === proposed)?.label
  const hint =
    proposed !== null && proposed !== value
      ? `Le complément de référence propose «\u00a0${proposedLabel ?? ''}\u00a0».`
      : complement !== null && proposed === null
        ? 'Complément non reconnu — le type de résidence reste à choisir.'
        : undefined

  return (
    <Field label="Type de résidence" hint={hint}>
      {({ labelId, hintId }) => (
        <Select
          aria-labelledby={labelId}
          aria-describedby={hintId}
          options={options}
          value={value}
          disabled={disabled}
          placeholder="À choisir"
          onValueChange={(choice) => onValueChange(choice === UNSET_RESIDENCE_TYPE ? null : choice)}
        />
      )}
    </Field>
  )
}
